import { useState, useEffect } from 'react';
import { useI18n } from '@/context/I18nContext';
import { CrudPage, type CrudColumn, type CrudField } from '@/components/crud/CrudPage';
import { rpc } from '@/api/client';

interface EngineProfile {
  id: string;
  brand: string;
  type: string;
}

interface Engine {
  id: string;
  engineProfileId: string;
  brand: string;
  type: string;
  nr?: string;
  teamId: string;
}

export function EnginesTab({ teamId, readOnly }: { teamId: string; readOnly?: boolean }) {
  const { t } = useI18n();
  const [profiles, setProfiles] = useState<EngineProfile[]>([]);

  useEffect(() => {
    rpc<EngineProfile[]>('ListEngineProfiles', {}).then(setProfiles).catch(console.error);
  }, []);

  const columns: CrudColumn<Engine>[] = [
    { key: 'brand', label: t('common.brand') },
    {
      key: 'type',
      label: t('common.type'),
      render: (e) => (
        <span className="inline-block px-2.5 py-0.5 bg-accent-subtle border border-accent/20 rounded-full text-xs font-medium text-accent-hover tracking-wide">
          {e.type}
        </span>
      ),
    },
    {
      key: 'nr',
      label: t('common.nr'),
      className: 'w-[80px] text-text-secondary tabular-nums text-sm',
      render: (e) => e.nr || <span className="text-text-muted">—</span>,
    },
  ];

  const fields: CrudField[] = [
    {
      key: 'engineProfileId',
      label: t('materials.profile'),
      type: 'select',
      placeholder: t('materials.selectProfile'),
      options: profiles.map(p => ({ value: p.id, label: `${p.brand} / ${p.type}` })),
    },
    { key: 'nr', label: t('common.nr'), placeholder: 'e.g. 1', info: t('common.nrInfo'), optional: true, defaultValue: (items) => String(items.length + 1) },
  ];

  return (
    <CrudPage<Engine>
      title={t('materials.tab.engines')}
      entityName={t('materials.entity.engine')}
      emptyIcon="🔧"
      columns={columns}
      fields={fields}
      listMethod="ListEngines"
      createMethod="CreateEngine"
      updateMethod="UpdateEngine"
      deleteMethod="DeleteEngine"
      listParams={{ teamId }}
      readOnly={readOnly}
      hiddenValues={{ teamId }}
    />
  );
}
